import styled from '@emotion/styled';
import Icon from '@ant-design/icons';
import { Button, Tag } from 'antd';
import { useTranslate } from '@tolgee/react';
import dayjs from 'lib/dayjs';
import HighlightText from 'views/PureLLM/Components/HighlightText';
import { ReactComponent as SolutionSvg } from 'views/PureLLM/assets/solution.svg';
import { ReactComponent as BulbSvg } from 'views/PureLLM/assets/bulb.svg';
import { getScenarioGroupTimeValue } from 'views/PureLLM/Hooks/useOfficialScenarioData';

export interface DetailHeaderProps {
  /** 分类名称 */
  category: string;
  /** 场景名称 */
  name: string;
  /** 更新时间 */
  updateTime?: string | number;
  /** 买家问法示例列表 */
  suggestedQuestions: string[];
  /** 搜索关键词（用于高亮） */
  searchKeyword?: string;
  /** 点击操作日志 */
  onOperationLogClick?: () => void;
  /** 隐藏标题栏（弹窗模式下由外层 title 代替） */
  hideTitleBar?: boolean;
}

function DetailHeader(props: DetailHeaderProps) {
  const {
    category,
    name,
    updateTime,
    suggestedQuestions,
    searchKeyword,
    onOperationLogClick,
    hideTitleBar,
  } = props;
  const { t } = useTranslate();
  const normalizedUpdateTime = getScenarioGroupTimeValue(updateTime);
  const formattedUpdateTime = normalizedUpdateTime
    ? dayjs(normalizedUpdateTime).format('YYYY/MM/DD HH:mm:ss')
    : '';

  return (
    <div
      data-sid="dh-root-01"
      className={hideTitleBar ? '' : 'p-4'}
      style={{ borderBottom: '1px solid #F0F2F5' }}
    >
      {!hideTitleBar && (
        <div
          data-sid="dh-titlebar-02"
          className={`flex items-center justify-between ${
            suggestedQuestions.length > 0 ? 'mb-3' : ''
          }`}
        >
          <div data-sid="dh-titlewrap-11" className="flex items-center gap-3 min-w-0">
            <h3 data-sid="dh-title-03" className="m-0 text-base font-medium text-gray-800">
              <HighlightText text={`${category} - ${name}`} keyword={searchKeyword} />
            </h3>
            {updateTime ? (
              <span
                data-sid="dh-updatetime-12"
                className="whitespace-nowrap"
                style={{
                  fontWeight: 400,
                  fontSize: 12,
                  color: '#B5B9BF',
                  lineHeight: '18px',
                }}
              >
                {t('scene.aiAgentConfig.scenarioDetail.label.updateTime')}:{formattedUpdateTime}
              </span>
            ) : null}
          </div>
          <LogButton
            data-sid="dh-logbtn-04"
            type="link"
            className="!font-normal !text-sm"
            icon={
              <Icon data-sid="dh-logicon-05" component={SolutionSvg} style={{ fontSize: 16 }} />
            }
            onClick={onOperationLogClick}
          >
            {t('scene.aiAgentConfig.operationLog.title')}
          </LogButton>
        </div>
      )}

      {suggestedQuestions.length > 0 && (
        <div
          data-sid="dh-questions-06"
          className="bg-[#F7F9FA] border border-[#F0F2F5] rounded p-3"
        >
          <div data-sid="dh-qheader-07" className="flex items-center gap-2 mb-2">
            <Icon data-sid="dh-qdot-08" component={BulbSvg} style={{ fontSize: 16 }} />
            <span
              data-sid="dh-qlabel-09"
              className="font-medium text-xs text-[#515359] leading-[18px]"
            >
              {t('scene.aiAgentConfig.scenarioDetail.label.buyerQuestionExample')}
            </span>
            <span
              data-sid="dh-qhint-10"
              className="font-normal text-xs text-[#84868C] leading-[18px]"
            >
              {t('scene.aiAgentConfig.scenarioDetail.description.questionHitScenario')}
            </span>
          </div>
          <QuestionTags data-sid="dh-qtags-11">
            {suggestedQuestions.map((question, index) => (
              <Tag data-sid="dh-qtag-12" key={index} color="default">
                {question}
              </Tag>
            ))}
          </QuestionTags>
        </div>
      )}
    </div>
  );
}

// 仅用于修改 antd Tag 组件内部样式
const QuestionTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  column-gap: 12px;
  row-gap: 8px;

  .ant-tag {
    margin: 0;
    padding: 3px 8px;
    background: #ffffff;
    border: 1px solid #d3d7e0;
    border-radius: 8px;
    font-weight: 400;
    font-size: 12px;
    color: #84868c;
    line-height: 18px;
  }
`;

const LogButton = styled(Button)`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0;
  height: auto;
  color: #515359;
  line-height: 20px;

  .anticon {
    margin-right: 0;
    color: inherit;
    transition: color 0.2s ease;
  }

  > span {
    margin-left: 0;
  }

  &:hover,
  &:focus,
  &:active {
    color: var(--ant-primary-color);
  }

  &:hover .anticon,
  &:focus .anticon,
  &:active .anticon {
    color: inherit;
  }
`;

export default DetailHeader;
